import { resolveActivationEndpoints, ACTIVATION_PRODUCT_CODE } from './activationConstants';
import { ActivationCertificateVerifier } from './ActivationCertificateVerifier';
import type { ActivationClaims, ActivationState } from './ActivationClaims';
import { isValidCommercialKey, normalizeCommercialKey } from './CommercialKey';
import { ClockTamperGuard } from './ClockTamperGuard';
import { persistLicenceBinding } from '@application/licence/ActivationInstallGuard';
import { SecureLicenceStore } from './SecureLicenceStore';
import { getAppVersion } from '@shared/constants/appVersion';
import { resolveDeviceInfo } from './DeviceInfo';
import { hasInstallDataConsent } from './installConsentStore';
import { INSTALL_DATA_POLICY_VERSION } from '@domain/legal/InstallDataPolicy';

export interface ActivationResult {
  ok: boolean;
  error?: string;
}

export type ActivationBeforePersist = (claims: ActivationClaims) => Promise<string | null>;

/** SEC C4 — parcours d’activation attendu côté API. */
export type ExpectedLicenseKeyType = 'cabinet' | 'client';

interface ActivationApiResponse {
  certificate?: string;
  server_time?: string;
  error?: string;
  code?: string;
}

type CallActivateResult = ActivationResult & { unreachable?: boolean };

export class ActivationService {
  constructor(
    private readonly endpoints = resolveActivationEndpoints(),
    private readonly verifier = new ActivationCertificateVerifier(),
  ) {}

  /** Portefeuille Cabinet — refuse une clé entreprise. */
  async activateCabinet(
    commercialKey: string,
    beforePersist?: ActivationBeforePersist,
  ): Promise<ActivationResult> {
    return this.activate(commercialKey, beforePersist, 'cabinet');
  }

  /** Entreprise / « Activer et ajouter » — refuse une clé Cabinet. */
  async activateClient(
    commercialKey: string,
    beforePersist?: ActivationBeforePersist,
  ): Promise<ActivationResult> {
    return this.activate(commercialKey, beforePersist, 'client');
  }

  async activate(
    commercialKey: string,
    beforePersist?: ActivationBeforePersist,
    expectedKeyType?: ExpectedLicenseKeyType,
  ): Promise<ActivationResult> {
    const normalized = normalizeCommercialKey(commercialKey);
    if (!isValidCommercialKey(normalized)) {
      return { ok: false, error: 'Format de clé commerciale invalide (NXS-XXXX-XXXX-XXXX-XXXX).' };
    }

    if (!hasInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
      return {
        ok: false,
        error:
          'Acceptez la politique de collecte de données avant d’activer (écran d’activation ou confidentialité).',
      };
    }

    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!fingerprint) {
      return { ok: false, error: 'Empreinte du poste indisponible. Relancez l’application.' };
    }

    let lastError = 'Connexion Internet requise pour l’activation initiale.';
    for (const endpoint of this.endpoints) {
      const result = await this.callActivate(
        endpoint,
        normalized,
        fingerprint,
        beforePersist,
        expectedKeyType,
      );
      if (result.ok) return result;
      if (!result.unreachable) {
        return { ok: false, error: result.error };
      }
      lastError = result.error ?? lastError;
    }

    return { ok: false, error: lastError };
  }

  private formatActivationError(data: ActivationApiResponse, status: number): string {
    if (data.error) return data.error;
    if (data.code === 'max_devices_reached') {
      return 'Nombre maximal de postes atteint. Révoquez un poste existant dans l’administration Nexxus ou désactivez un ancien poste.';
    }
    if (data.code === 'cabinet_key_required') {
      return 'Cette clé est une clé entreprise. Elle ne peut pas activer un portefeuille Cabinet.';
    }
    if (data.code === 'client_key_required') {
      return 'Cette clé est une clé Cabinet. Elle ne peut pas ajouter une entreprise cliente.';
    }
    return `Activation refusée par le serveur (${status}).`;
  }

  private formatCertificateError(code: ActivationState['errorCode']): string {
    switch (code) {
      case 'DEVICE_MISMATCH':
        return 'Le certificat retourné ne correspond pas à ce poste. Relancez l’activation ou transférez la licence vers l’empreinte affichée.';
      case 'TENANT_MISMATCH':
        return 'Le certificat retourné appartient à un autre client.';
      case 'EXPIRED':
        return 'La licence est arrivée à expiration.';
      case 'NOT_YET_VALID':
        return 'Le certificat n’est pas encore valide. Vérifiez la date et l’heure de Windows.';
      default:
        return 'La signature du certificat reçu est invalide. Contactez le support Nexxus.';
    }
  }

  private devicePayload() {
    return resolveDeviceInfo();
  }

  private async postActivation(
    endpoint: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; status: number; data: ActivationApiResponse; unreachable: boolean }> {
    if (window.electron?.activateRemote) {
      try {
        const result = await window.electron.activateRemote(endpoint, body);
        return {
          ok: result.ok,
          status: result.status,
          data: result.data as ActivationApiResponse,
          unreachable: false,
        };
      } catch {
        /* IPC indisponible ou erreur réseau processus principal → repli fetch renderer. */
      }
    }

    try {
      const res = await this.fetchWithTimeout(endpoint, body);
      const data = (await res.json().catch(() => ({}))) as ActivationApiResponse;
      return { ok: res.ok, status: res.status, data, unreachable: false };
    } catch {
      return { ok: false, status: 0, data: {}, unreachable: true };
    }
  }

  /** Compatible Electron 2.3.x (AbortSignal.timeout parfois indisponible en renderer). */
  private fetchWithTimeout(endpoint: string, body: Record<string, unknown>): Promise<Response> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 30_000);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    }).finally(() => window.clearTimeout(timer));
  }

  private async callActivate(
    endpoint: string,
    commercialKey: string,
    fingerprint: string,
    beforePersist?: ActivationBeforePersist,
    expectedKeyType?: ExpectedLicenseKeyType,
  ): Promise<CallActivateResult> {
    const body = {
      commercial_key: commercialKey,
      device_fingerprint: fingerprint,
      product_code: ACTIVATION_PRODUCT_CODE,
      app_version: getAppVersion(),
      ...(expectedKeyType ? { expected_key_type: expectedKeyType } : {}),
      ...(await this.devicePayload()),
    };

    const { ok, status, data, unreachable } = await this.postActivation(endpoint, body);
    if (unreachable) {
      return { ok: false, error: 'Serveur d’activation injoignable.', unreachable: true };
    }

    if (!ok) {
      return {
        ok: false,
        error: this.formatActivationError(data, status),
        unreachable: false,
      };
    }

    if (!data.certificate) {
      return { ok: false, error: 'Réponse serveur incomplète.' };
    }

    const serverTimeMs = data.server_time ? Date.parse(data.server_time) : Number.NaN;
    const trustedNowSec = Number.isFinite(serverTimeMs)
      ? Math.floor(serverTimeMs / 1000)
      : Math.floor(Date.now() / 1000);
    const check = await this.verifier.verifyCertificate(
      data.certificate,
      fingerprint,
      '',
      0,
      trustedNowSec,
    );
    if (!check.valid || !check.claims) {
      return { ok: false, error: this.formatCertificateError(check.errorCode) };
    }

    if (beforePersist) {
      const blocked = await beforePersist(check.claims);
      if (blocked) {
        return { ok: false, error: blocked };
      }
    }

    await SecureLicenceStore.upsertPortfolioCertificate(check.claims.tenant_id, data.certificate);
    await persistLicenceBinding(check.claims);
    if (data.server_time) {
      await ClockTamperGuard.recordTrustedServerTime(data.server_time);
    }
    await SecureLicenceStore.setLastRenewalAt(check.claims.tenant_id, new Date().toISOString());
    await SecureLicenceStore.setLastVerifiedAt(check.claims.tenant_id, new Date().toISOString());
    return { ok: true };
  }
}
